/**
 * Medical Necessity screen UX.
 *
 * Loads the order for header context and hosts the PDF + NCCN review panels.
 * Order demographics / clinical PDF path come from the Postgres-backed
 * single-order data accumulator; `document_page_mapping` is still read from
 * Firestore (not yet reliable on the Postgres general-order payload).
 * @module medicalNecessityAnalysis
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getOrderRegimenIcd } from "../../api/postCall/orderRegimenIcd";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { useDataAccumulatorForSingleOrder } from "../../context/dataAccumulatorForSingleOrder";
import { LoadedNccnEvaluation } from "../../data-model/nccnEvaluation";
import { useDocumentPageMapping } from "../../hooks/useDocumentPageMapping";
import { logError } from "../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { useDocumentManagement } from "../finalDocuments/hooks";
import { useMedicalNecessityAnalytics } from "./analytics/useMedicalNecessityAnalytics";
import { MedicalNecessityAnalysisHeader } from "./components/MedicalNecessityAnalysisHeader";
import { MedicalNecessityPdfPanel } from "./components/MedicalNecessityPdfPanel";
import { NccnEvaluationPanel } from "./components/NccnEvaluationPanel";
import { PanelDivider } from "./components/PanelDivider";
import { PdfNavigationProvider } from "./context/PdfNavigationContext";
import { PdfProofCaptureProvider } from "./context/PdfProofCaptureContext";
import {
  loadEvaluationPayload,
  toTitleCase,
} from "./utils/loadEvaluationPayload";
import { extractRegimenCode } from "./utils/nccnCaseGate";
import {
  findFirstUnreviewedMandatoryStep,
  getGuidelineDisplayPositions,
} from "./utils/nccnReviewFeedback";

/** Default Document Pane width as a percentage of the analysis row. */
const DEFAULT_PDF_PANEL_PCT = 40;
/** Smallest allowed Document Pane width (keeps the evaluation panel usable). */
const MIN_PDF_PANEL_PCT = 25;
/** Largest allowed Document Pane width (keeps the document pane usable). */
const MAX_PDF_PANEL_PCT = 75;

/**
 * Top-level Medical Necessity screen. Loads the order behind `:orderId`,
 * shows the header, and hosts the analysis body.
 */
const MedicalNecessityAnalysis: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  // Document Pane width as a percentage of the analysis row. Local state only:
  // the split resets to the default on every visit.
  const [pdfPanelPct, setPdfPanelPct] = useState(DEFAULT_PDF_PANEL_PCT);
  const analysisRowRef = useRef<HTMLDivElement>(null);

  const {
    generalOrderDetails,
    generalOrderDetailsLoading: isOrderLoading,
    submissionData,
    abortAll,
    fetchAll,
  } = useDataAccumulatorForSingleOrder();

  const order = generalOrderDetails?.order ?? null;
  const orgId = getOrgIdForFetchExternalWorklist() || undefined;

  const { documentPageMapping } = useDocumentPageMapping(orderId);

  const {
    pdfUrl,
    isLoading: isDocLoading,
    error: docError,
  } = useDocumentManagement({
    order: generalOrderDetails?.order ?? null,
    isLoadingOrder: isOrderLoading ?? false,
    orderId: orderId ?? "",
    skipDocumentDownload: true,
  });

  const [evaluation, setEvaluation] = useState<LoadedNccnEvaluation | null>(
    null,
  );
  const [isEvaluationLoading, setIsEvaluationLoading] = useState(false);
  const [regimenName, setRegimenName] = useState<string | undefined>(undefined);
  const [regimenIcdCode, setRegimenIcdCode] = useState<string | undefined>(
    undefined,
  );

  const patientIdForAnalytics =
    order?.demographics?.patient_id ??
    order?.demographics?.patient_mrn ??
    undefined;

  const analytics = useMedicalNecessityAnalytics({
    orderId: orderId ?? "",
    patientId: patientIdForAnalytics,
  });

  // Fires `panel_opened` once the evaluation is loaded and the panel is usable.
  // The resume position is the first unreviewed mandatory step (1 for a fresh
  // order). Reset on order change so a route-param swap re-fires.
  const panelOpenedRef = useRef(false);
  useEffect(() => {
    panelOpenedRef.current = false;
  }, [orderId]);
  useEffect(() => {
    if (!evaluation || panelOpenedRef.current) return;
    panelOpenedRef.current = true;
    const guidelines = evaluation.payload.guidelines ?? [];
    const resume = findFirstUnreviewedMandatoryStep(evaluation.payload);
    const displayPositions = getGuidelineDisplayPositions(guidelines);
    const guidelineIndex =
      displayPositions[resume.guidelineIndex] ?? resume.guidelineIndex + 1;
    analytics.trackPanelOpened(guidelines.length, guidelineIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per panel open
  }, [evaluation]);

  useEffect(() => {
    if (orderId && orgId) {
      fetchAll(orderId, orgId, { force: false });
    }
    return () => abortAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, orgId]);

  useEffect(() => {
    if (!orderId) return undefined;
    let isActive = true;
    setRegimenName(undefined);
    setRegimenIcdCode(undefined);
    getOrderRegimenIcd({ order_id: orderId, org_id: orgId ?? null })
      .then((response) => {
        if (!isActive) return;
        setRegimenName(extractRegimenCode(response.regimen_name) ?? undefined);
        const firstIcd = response.icd_codes?.[0]?.code?.trim();
        setRegimenIcdCode(firstIcd || undefined);
      })
      .catch((error) => {
        logError(error as Error, "Error loading order regimen for header");
      });
    return () => {
      isActive = false;
    };
  }, [orderId, orgId]);

  useEffect(() => {
    if (!orderId) return undefined;
    let isActive = true;
    setIsEvaluationLoading(true);
    setEvaluation(null);
    loadEvaluationPayload(orderId, orgId)
      .then((payload) => {
        if (isActive) setEvaluation(payload);
      })
      .catch((error) => {
        logError(error as Error, "Error loading NCCN evaluation on mount");
        if (isActive) setEvaluation(null);
      })
      .finally(() => {
        if (isActive) setIsEvaluationLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [orderId, orgId]);

  const patientName = useMemo(() => {
    if (!order?.demographics) return "";
    const { first_name, last_name } = order.demographics;
    const fullName = [first_name, last_name].filter(Boolean).join(" ");
    return fullName ? toTitleCase(fullName) : "—";
  }, [order]);

  const patientId =
    order?.demographics?.patient_id ?? order?.demographics?.patient_mrn ?? "—";

  // Prefer validated primary DX from Postgres submission; fall back to
  // regimen/conditions ICD list used by the order-regimen-icd endpoint.
  const icdCode =
    submissionData?.submission?.primary_diagnosis_details?.[0]?.icd_code ??
    regimenIcdCode ??
    undefined;

  if (isOrderLoading) {
    return <LoaderMessage message="Loading order..." />;
  }

  if (!order) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <p className="text-gray-600">Order not found.</p>
      </div>
    );
  }

  return (
    <div className="medical-necessity-analysis flex h-full w-full flex-col overflow-hidden bg-white">
      <MedicalNecessityAnalysisHeader
        onClose={() => {
          analytics.trackPanelClosed(
            evaluation?.ai_evaluation,
            evaluation?.order_evaluation_result,
          );
          navigate(`/worklists/singleOrder/${orderId}`);
        }}
        patientName={patientName}
        patientId={patientId}
        icdCode={icdCode}
        regimenName={regimenName}
      />
      <div
        ref={analysisRowRef}
        className="medical-necessity-analysis--main flex min-h-0 flex-1 flex-row overflow-hidden"
      >
        <PdfNavigationProvider>
          <PdfProofCaptureProvider orderId={orderId ?? ""}>
            <MedicalNecessityPdfPanel
              pdfUrl={pdfUrl}
              isLoading={isDocLoading}
              error={docError}
              documentPageMapping={documentPageMapping}
              analytics={analytics}
              widthPct={pdfPanelPct}
              visitDate={
                order.clinical_document?.visit_date
              }
            />
            <PanelDivider
              value={pdfPanelPct}
              min={MIN_PDF_PANEL_PCT}
              max={MAX_PDF_PANEL_PCT}
              onChange={setPdfPanelPct}
              onReset={() => setPdfPanelPct(DEFAULT_PDF_PANEL_PCT)}
              containerRef={analysisRowRef}
            />
            <NccnEvaluationPanel
              evaluation={evaluation}
              isLoading={isEvaluationLoading}
              orderId={orderId ?? ""}
              patientId={patientId !== "—" ? patientId : undefined}
              orgId={orgId}
              analytics={analytics}
            />
          </PdfProofCaptureProvider>
        </PdfNavigationProvider>
      </div>
    </div>
  );
};

export default MedicalNecessityAnalysis;
